import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, GraduationCap, FileText, HelpCircle, ThumbsDown, AlertTriangle } from "lucide-react";
import "@/mockups/deepcoast.css";
import "@/components/ai2/ai2.css";
import { Section } from "@/mockups/dc";
import { AI2 } from "@/components/ai2/api";
import { LearnedItem } from "@/components/ai2/LearnedItem";

const TABS = [["rules", "Corrections"], ["examples", "Trusted examples"], ["down", "Thumbs-down answers"], ["kb", "Knowledge base"]];

const List = ({ items, kind, onChanged, empty }) => {
  const byId = Object.fromEntries(items.map((i) => [i.id, i]));
  const order = { pending: 0, active: 1, disabled: 2 };
  const sorted = [...items].sort((a, b) => (b.conflicts_with?.length || 0) - (a.conflicts_with?.length || 0) || order[a.status] - order[b.status]);
  if (!items.length) return <div className="muted" style={{ fontSize: 13 }} data-testid={`ai2-${kind}-empty`}>{empty}</div>;
  return <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{sorted.map((i) => <LearnedItem key={i.id} item={i} kind={kind} onChanged={onChanged} byId={byId} />)}</div>;
};

const KB = ({ d }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, alignItems: "start" }}>
    <Section title="Most-used documents" sub="How often answers cited each document" icon={FileText} testid="ai2-kb-used">
      {!d.kb_used.length && <div className="muted" style={{ fontSize: 13 }}>No documents cited yet.</div>}
      {d.kb_used.map((k) => <div key={k.kb_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--divider)" }}><span>{k.title}</span><b className="mono">{k.count}</b></div>)}
    </Section>
    <Section title="Unanswered gaps" sub="Questions where no matching document was found. Consider adding a document." icon={HelpCircle} testid="ai2-kb-gaps">
      {!d.kb_gaps.length && <div className="muted" style={{ fontSize: 13 }}>No gaps found yet.</div>}
      {d.kb_gaps.map((g) => <div key={g.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--divider)" }}>{g.question}<div className="muted" style={{ fontSize: 11 }}>searched "{g.query}" · {g.user_name} · {new Date(g.created_at).toLocaleDateString()}</div></div>)}
    </Section>
    <Section title="Documents to update" sub="A correction overrode what the document says" icon={AlertTriangle} testid="ai2-kb-conflicts">
      {!d.kb_conflicts.length && <div className="muted" style={{ fontSize: 13 }}>No conflicts flagged.</div>}
      {d.kb_conflicts.map((c) => <div key={c.kb_id + c.rule_id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--divider)" }}><b>{c.title}</b><div className="muted" style={{ fontSize: 12 }}>Correction: {c.rule}</div></div>)}
    </Section>
  </div>
);

export default function AILearning() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("rules");
  const load = useCallback(() => axios.get(`${AI2}/learning`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Couldn't load")), []);
  useEffect(() => { load(); }, [load]);
  const pending = d ? [...d.rules, ...d.examples].filter((x) => x.status === "pending").length : 0;
  const conflicts = d ? d.rules.filter((r) => r.conflicts_with?.length).length : 0;
  return (
    <div className="dc v3" style={{ background: "transparent", maxWidth: 1100 }} data-testid="ai2-learning-page">
      <Link to="/ai-insights" className="btn btn-sm" style={{ textDecoration: "none", marginBottom: 12 }} data-testid="ai2-learning-back"><ArrowLeft size={14} />Back to AI Insights</Link>
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 6px", display: "flex", alignItems: "center", gap: 10 }}><GraduationCap size={28} color="#1F8A7C" />What AI has learned</h1>
      <p className="muted" style={{ fontSize: 14, margin: "0 0 16px" }}>Corrections and approved answers the assistant checks before answering{d ? ` at ${d.location}` : ""}. Manager and admin corrections go live immediately; staff corrections wait here for approval.</p>
      {err && <div className="alert" data-testid="ai2-learning-error">{err}</div>}
      {!d && !err && <div className="skeleton" style={{ height: 300, borderRadius: 12 }} />}
      {d && (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }} className="mono" data-testid="ai2-learning-summary">
            <span className="ai2-pill"><b>{d.rules.length}</b> corrections</span>
            <span className="ai2-pill"><b>{d.examples.length}</b> trusted examples</span>
            <span className="ai2-pill" style={{ color: pending ? "#B7791F" : undefined }}><b>{pending}</b> waiting for approval</span>
            <span className="ai2-pill" style={{ color: conflicts ? "#C2483A" : undefined }}><b>{conflicts}</b> conflicts</span>
          </div>
          <div className="tabs" style={{ marginBottom: 16 }} data-testid="ai2-learning-tabs">
            {TABS.map(([k, l]) => <button key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`ai2-learning-tab-${k}`}>{l}</button>)}
          </div>
          {tab === "rules" && <List items={d.rules} kind="rules" onChanged={load} empty="No corrections yet. Tap thumbs-down on an answer to teach the assistant." />}
          {tab === "examples" && <List items={d.examples} kind="examples" onChanged={load} empty="No trusted examples yet. Tap thumbs-up on a good answer." />}
          {tab === "down" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="ai2-thumbs-down">
              {!d.thumbs_down.length && <div className="muted" style={{ fontSize: 13 }}>No thumbs-down answers.</div>}
              {d.thumbs_down.map((m) => (
                <div key={m.id} className="card" style={{ padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, display: "flex", gap: 6, alignItems: "center" }}><ThumbsDown size={13} color="#C2483A" />{m.question}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6, maxHeight: 90, overflow: "hidden", whiteSpace: "pre-wrap" }}>{m.content}</div>
                  {m.correction && <div style={{ fontSize: 13, marginTop: 6 }}>Correction: <b>{m.correction}</b></div>}
                </div>
              ))}
            </div>
          )}
          {tab === "kb" && <KB d={d} />}
        </>
      )}
    </div>
  );
}
